import React from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';

interface ConsolePageStat {
  label: string;
  value: React.ReactNode;
  helper?: React.ReactNode;
  tone?: 'default' | 'success' | 'warning';
}

interface ConsolePageShellProps {
  title: string;
  subtitle: React.ReactNode;
  eyebrow?: string;
  actions?: React.ReactNode;
  stats?: ConsolePageStat[];
  children: React.ReactNode;
}

const toneStyles = (tone?: ConsolePageStat['tone']) => {
  if (tone === 'success') {
    return { bg: 'rgba(var(--success-rgb),0.12)', color: 'success.main' };
  }
  if (tone === 'warning') {
    return { bg: 'rgba(var(--warning-rgb),0.12)', color: 'warning.main' };
  }
  return { bg: 'background.default', color: 'text.primary' };
};

export const ConsolePageShell: React.FC<ConsolePageShellProps> = ({
  title,
  subtitle,
  eyebrow = 'Console View',
  actions,
  stats = [],
  children,
}) => {
  return (
    <Stack spacing={2}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 0,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1fr) auto' },
            gap: 1.5,
            px: { xs: 1.5, sm: 2, lg: 2.5 },
            py: { xs: 1.5, sm: 1.75, lg: 2 },
            borderBottom: stats.length ? '1px solid' : 0,
            borderColor: 'divider',
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '0.72rem',
                fontWeight: 900,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: 'text.secondary',
                mb: 0.5,
              }}
            >
              {eyebrow}
            </Typography>
            <Typography variant="h5" fontWeight={900} sx={{ lineHeight: 1.1, mb: 0.45 }}>
              {title}
            </Typography>
            <Typography sx={{ color: 'text.secondary', maxWidth: 860 }}>
              {subtitle}
            </Typography>
          </Box>

          {actions ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center" justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}>
              {actions}
            </Stack>
          ) : null}
        </Box>

        {stats.length ? (
          <Box
            sx={{
              px: { xs: 1.5, sm: 2, lg: 2.5 },
              py: 1.2,
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', md: `repeat(${Math.min(stats.length, 4)}, minmax(0, 1fr))` },
              gap: 1,
              bgcolor: 'background.default',
            }}
          >
            {stats.map((stat) => {
              const tone = toneStyles(stat.tone);
              return (
                <Box
                  key={String(stat.label)}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: tone.bg,
                    px: 1.15,
                    py: 1,
                    minWidth: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.64rem',
                      fontWeight: 900,
                      letterSpacing: '0.08em',
                      textTransform: 'uppercase',
                      color: 'text.disabled',
                    }}
                    noWrap
                  >
                    {stat.label}
                  </Typography>
                  <Typography sx={{ mt: 0.45, fontSize: '0.98rem', fontWeight: 900, color: tone.color }} noWrap>
                    {stat.value}
                  </Typography>
                  <Typography sx={{ mt: 0.2, fontSize: '0.72rem', color: 'text.secondary' }} noWrap>
                    {stat.helper || '—'}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        ) : null}
      </Paper>

      {children}
    </Stack>
  );
};
